import {Logger, Category} from '@simpleinvest/front-end-logger';
import {Constants} from '@client/util/constants';
import {toHeaderCurrency as formatCurrency, abbreviateNumber} from '@client/util/format';
// eslint-disable-next-line import/no-cycle
import overview from '@client/services/overview';

const logger = new Logger({
  category: Category.Accounts,
  fileName: 'overview.js',
});

const stateObj = {
  loaded: false,
  userSummary: {
    netWorth: 0,
    totalAssets: 0,
    totalLiabilites: 0,
    cash: 0,
  },
  riskInfo: {
    riskToleranceId: null,
    isComplete: null,
    userScore: null,
    score: null,
    isOverride: null,
    overrideScore: null,
    lastDate: null,
  },
  advisorContactInfo: {
    email: '',
    phone: '',
    advisorNameAndTitle: '',
  },
  riskInfoLoaded: false,
  isAdvisorContactInfoLoaded: false,
};

const mutations = {
  setUserSummary(state, userSummary) {
    state.userSummary = userSummary;
  },
  setRiskInfo(state, riskInfo) {
    state.riskInfo = riskInfo;
    state.riskInfoLoaded = true;
  },
  setIsLoaded(state, loaded) {
    state.loaded = loaded;
  },
  setRiskInfoLoaded(state, isRiskScoreLoaded) {
    state.riskInfoLoaded = isRiskScoreLoaded;
  },
  SET_ADVISOR_CONTACT_INFO_LOADED(state, loaded) {
    state.isAdvisorContactInfoLoaded = loaded;
  },
  SET_ADVISOR_CONTACT_INFO(state, contactInfo) {
    state.advisorContactInfo = contactInfo;
  },
};

const actions = {
  async getOverviewData(context, {userId}) {
    const {commit} = context;

    try {
      let response;

      try {
        response = await overview.getOverviewData(userId);
      } catch {
        logger.addBreadcrumb({callingFuncName: 'getOverviewData', message: 'getOverviewData error caught - retrying'});
        response = await overview.getOverviewData(userId);
      }

      commit('accountList/saveAccountList', response.data.data.accountInfo, {root: true});
      commit('goalList/saveGoalList', response.data.data.goalInfo, {root: true});
      commit('setUserSummary', response.data.data.userSummary);
      commit('accountAggregation/setAccountAggregation', response.data.data.aggregationInfo, {root: true});
      commit('setIsLoaded', true);
      commit('setRiskInfo', response.data.data.rtqInfo);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getOverviewData',
        message: `getOverviewData error caught`,
      });
      logger.logException({
        error,
        fingerprint: ['getOverviewData'],
      });
    }
  },
  setRiskInfoDirty(context) {
    context.commit('setRiskInfoLoaded', false);
  },
  async updateUserSummary({commit}, {userId}) {
    try {
      const response = await overview.getUserSummaryForUserId(userId);

      if (response.data.data.userSummary) {
        commit('setUserSummary', response.data.data.userSummary);
      }
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'updateUserSummary',
        message: `getUserSummaryForUserId error caught`,
      });
      logger.logException({
        error,
        fingerprint: ['updateUserSummary'],
      });
    }
  },
  async getAdvisorContactInfo({commit, rootGetters}) {
    const riaUserId = rootGetters['user/riaUserId'];
    const riaId = rootGetters['cognitoUser/riaId'];

    try {
      const response = await overview.getContactUsInfo(riaId, riaUserId);

      commit('SET_ADVISOR_CONTACT_INFO_LOADED', true);
      commit('SET_ADVISOR_CONTACT_INFO', response.data.data);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getAdvisorContactInfo',
        message: 'getAdvisorContactInfo error caught',
      });
      logger.logException({
        error,
        fingerprint: ['getAdvisorContactInfo'],
      });

      commit('SET_ADVISOR_CONTACT_INFO_LOADED', false);
    }
  },
};

const gettersObj = {
  isOverviewDataLoaded: (state, getters, rootState, rootGetters) => {
    const isGoalListLoaded = rootGetters['goalList/isGoalListLoaded'];
    const isAccountListLoaded = rootGetters['accountList/isAccountListLoaded'];
    const isRiskScoreLoaded = getters.isRiskInfoLoaded;
    const isBankAccountsLoaded = rootGetters['bankAccount/loaded'];

    return state.loaded && isGoalListLoaded && isAccountListLoaded && isRiskScoreLoaded && isBankAccountsLoaded;
  },
  isUserSummaryLoaded: (state) => state.loaded,
  formattedNetWorth: (state) => {
    const netWorthPrefix = state.userSummary.netWorth < 0 ? '-' : '';

    return `${netWorthPrefix}${Constants.CURRENCY}${abbreviateNumber(Math.abs(state.userSummary.netWorth))}`;
  },
  formattedAggregationAssets: (state) => `${Constants.CURRENCY}${abbreviateNumber(state.userSummary.totalAssets)}`,
  formattedLiability: (state) => {
    const liabilityPrefix = state.userSummary.totalLiabilites ? '-' : '';

    return `${liabilityPrefix}${Constants.CURRENCY}${abbreviateNumber(state.userSummary.totalLiabilites)}`;
  },
  liability: (state) => state.userSummary.totalLiabilites,
  formattedAssets: (state) => `${Constants.CURRENCY}${formatCurrency(state.userSummary.totalAssets)}`,
  netWorth: (state) => state.userSummary.netWorth,
  formattedCash: (state) => `${Constants.CURRENCY}${formatCurrency(state.userSummary.cash)}`,
  riskToleranceScore: (state) => state.riskInfo.score,
  isRiskInfoLoaded: (state) => state.riskInfoLoaded,
  advisorContactInfo: (state) => state.advisorContactInfo,
  isAdvisorContactInfoLoaded: (state) => state.isAdvisorContactInfoLoaded,
  userSummaryCash: (state) => state.userSummary.cash,
};

export default {
  namespaced: true,
  state: stateObj,
  mutations,
  actions,
  getters: gettersObj,
};
